import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Users,
  Building2,
  Shield,
  Plus,
  Pencil,
  Trash2,
  ToggleLeft,
  ToggleRight,
} from 'lucide-react';
import { useUsers, useBranches, useRoles } from '../../api/queries/settings.queries';
import {
  useCreateUser,
  useUpdateUser,
  useDeleteUser,
  useCreateBranch,
  useUpdateBranch,
  useDeleteBranch,
  type CreateUserInput,
  type CreateBranchInput,
} from '../../api/mutations/settings.mutations';

type TabType = 'users' | 'branches' | 'roles';

function SettingsPage() {
  const { t, i18n } = useTranslation();
  const [activeTab, setActiveTab] = useState<TabType>('users');
  const [userModal, setUserModal] = useState<{
    isOpen: boolean;
    mode: 'create' | 'edit';
    userId?: string;
  }>({ isOpen: false, mode: 'create' });
  const [branchModal, setBranchModal] = useState<{
    isOpen: boolean;
    mode: 'create' | 'edit';
    branchId?: string;
  }>({ isOpen: false, mode: 'create' });

  // Fetch data
  const { data: users, refetch: refetchUsers } = useUsers();
  const { data: branches, refetch: refetchBranches } = useBranches();
  const { data: roles } = useRoles();

  // User modal state
  const [userForm, setUserForm] = useState({
    email: '',
    password: '',
    name: '',
    role: 'POS_OPERATOR',
    branchId: '',
    isActive: true,
  });

  // Branch modal state
  const [branchForm, setBranchForm] = useState({
    nameEn: '',
    nameTh: '',
    code: '',
    address: '',
    phone: '',
    type: 'branch' as 'branch' | 'warehouse',
    isActive: true,
  });

  const createUserMutation = useCreateUser();
  const updateUserMutation = useUpdateUser(userModal.userId || '');

  const createBranchMutation = useCreateBranch();
  const updateBranchMutation = useUpdateBranch(branchModal.branchId || '');

  const getLangValue = (multiLang: any) => {
    return multiLang?.[i18n.language as 'en' | 'th'] || multiLang?.en || '';
  };

  const handleCreateUser = async () => {
    const payload: CreateUserInput = {
      email: userForm.email,
      password: userForm.password,
      name: userForm.name,
      role: userForm.role,
      branchId: userForm.branchId || undefined,
      isActive: userForm.isActive,
    };

    try {
      await createUserMutation.mutateAsync(payload);
      setUserModal({ isOpen: false, mode: 'create' });
      resetUserForm();
      refetchUsers();
    } catch (error) {
      console.error('Failed to create user:', error);
    }
  };

  const handleUpdateUser = async () => {
    if (!userModal.userId) return;

    try {
      await updateUserMutation.mutateAsync({
        email: userForm.email,
        name: userForm.name,
        role: userForm.role,
        branchId: userForm.branchId || undefined,
        isActive: userForm.isActive,
      });
      setUserModal({ isOpen: false, mode: 'create' });
      resetUserForm();
      refetchUsers();
    } catch (error) {
      console.error('Failed to update user:', error);
    }
  };

  const handleDeleteUser = async (userId: string) => {
    if (!confirm(t('settings.confirmDeleteUser'))) return;

    const mutation = useDeleteUser(userId);
    try {
      await mutation.mutateAsync();
      refetchUsers();
    } catch (error) {
      console.error('Failed to delete user:', error);
    }
  };

  const handleCreateBranch = async () => {
    const payload: CreateBranchInput = {
      name: { en: branchForm.nameEn, th: branchForm.nameTh },
      code: branchForm.code,
      address: branchForm.address,
      phone: branchForm.phone,
      type: branchForm.type,
      isActive: branchForm.isActive,
    };

    try {
      await createBranchMutation.mutateAsync(payload);
      setBranchModal({ isOpen: false, mode: 'create' });
      resetBranchForm();
      refetchBranches();
    } catch (error) {
      console.error('Failed to create branch:', error);
    }
  };

  const handleUpdateBranch = async () => {
    if (!branchModal.branchId) return;

    try {
      await updateBranchMutation.mutateAsync({
        name: { en: branchForm.nameEn, th: branchForm.nameTh },
        code: branchForm.code,
        address: branchForm.address,
        phone: branchForm.phone,
        type: branchForm.type,
        isActive: branchForm.isActive,
      });
      setBranchModal({ isOpen: false, mode: 'create' });
      resetBranchForm();
      refetchBranches();
    } catch (error) {
      console.error('Failed to update branch:', error);
    }
  };

  const handleDeleteBranch = async (branchId: string) => {
    if (!confirm(t('settings.confirmDeleteBranch'))) return;

    const mutation = useDeleteBranch(branchId);
    try {
      await mutation.mutateAsync();
      refetchBranches();
    } catch (error) {
      console.error('Failed to delete branch:', error);
    }
  };

  const resetUserForm = () => {
    setUserForm({
      email: '',
      password: '',
      name: '',
      role: 'POS_OPERATOR',
      branchId: '',
      isActive: true,
    });
  };

  const resetBranchForm = () => {
    setBranchForm({
      nameEn: '',
      nameTh: '',
      code: '',
      address: '',
      phone: '',
      type: 'branch',
      isActive: true,
    });
  };

  const openEditUser = (user: any) => {
    setUserForm({
      email: user.email,
      password: '',
      name: user.name,
      role: user.role,
      branchId: user.branchId || '',
      isActive: user.isActive,
    });
    setUserModal({ isOpen: true, mode: 'edit', userId: user._id });
  };

  const openEditBranch = (branch: any) => {
    const addressStr = typeof branch.address === 'string'
      ? branch.address
      : `${branch.address.street}, ${branch.address.district}, ${branch.address.province}`;
    setBranchForm({
      nameEn: branch.name.en,
      nameTh: branch.name.th,
      code: branch.code,
      address: addressStr,
      phone: branch.phone,
      type: branch.type,
      isActive: branch.isActive,
    });
    setBranchModal({ isOpen: true, mode: 'edit', branchId: branch._id });
  };

  const getRoleColor = (role: string) => {
    switch (role) {
      case 'HQ_ADMIN':
        return 'bg-purple-100 text-purple-700';
      case 'HQ_PURCHASING':
        return 'bg-blue-100 text-blue-700';
      case 'BRANCH_MANAGER':
        return 'bg-green-100 text-green-700';
      case 'BRANCH_SUPERVISOR':
        return 'bg-yellow-100 text-yellow-700';
      case 'WAREHOUSE_STAFF':
        return 'bg-orange-100 text-orange-700';
      case 'POS_OPERATOR':
        return 'bg-gray-100 text-gray-700';
      default:
        return 'bg-gray-100 text-gray-700';
    }
  };

  const tabs = [
    { id: 'users' as TabType, label: t('settings.users'), icon: Users },
    { id: 'branches' as TabType, label: t('settings.branches'), icon: Building2 },
    { id: 'roles' as TabType, label: t('settings.roles'), icon: Shield },
  ];

  return (
    <div className="h-full flex flex-col bg-background">
      {/* Header */}
      <div className="border-b bg-card p-4">
        <h1 className="text-2xl font-bold">{t('settings.title')}</h1>
        <p className="text-sm text-muted-foreground mt-1">{t('settings.subtitle')}</p>
      </div>

      {/* Toolbar */}
      <div className="border-b bg-card p-4 flex items-center justify-between">
        <div className="flex items-center gap-1 bg-secondary/30 rounded-lg p-1">
          {tabs.map((tab) => {
            const Icon = tab.icon;
            return (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`flex items-center gap-2 px-3 py-1.5 rounded text-sm font-medium transition-colors ${
                  activeTab === tab.id
                    ? 'bg-primary text-primary-foreground'
                    : 'hover:bg-secondary'
                }`}
              >
                <Icon className="h-4 w-4" />
                {tab.label}
              </button>
            );
          })}
        </div>

        {activeTab === 'users' && (
          <button
            onClick={() => {
              resetUserForm();
              setUserModal({ isOpen: true, mode: 'create' });
            }}
            className="rounded-lg bg-primary px-4 py-2 text-sm font-medium text-primary-foreground hover:bg-primary/90 transition-colors flex items-center gap-2"
          >
            <Plus className="h-4 w-4" />
            {t('settings.createUser')}
          </button>
        )}

        {activeTab === 'branches' && (
          <button
            onClick={() => {
              resetBranchForm();
              setBranchModal({ isOpen: true, mode: 'create' });
            }}
            className="rounded-lg bg-primary px-4 py-2 text-sm font-medium text-primary-foreground hover:bg-primary/90 transition-colors flex items-center gap-2"
          >
            <Plus className="h-4 w-4" />
            {t('settings.createBranch')}
          </button>
        )}
      </div>

      {/* Content */}
      <div className="flex-1 overflow-y-auto p-6">
        <div className="max-w-6xl mx-auto">
          {/* Users Tab */}
          {activeTab === 'users' && (
            <div className="space-y-3">
              {users?.map((user) => (
                <div
                  key={user._id}
                  className="bg-card rounded-lg border p-4 flex items-center justify-between"
                >
                  <div className="flex items-center gap-4">
                    <div
                      className={`w-10 h-10 rounded-full flex items-center justify-center ${getRoleColor(
                        user.role
                      )}`}
                    >
                      <Users className="h-5 w-5" />
                    </div>
                    <div>
                      <div className="flex items-center gap-2">
                        <p className="font-medium">{user.name}</p>
                        <span className={`text-xs px-2 py-0.5 rounded ${getRoleColor(user.role)}`}>
                          {user.role}
                        </span>
                        {user.isActive ? (
                          <span className="text-xs px-2 py-0.5 rounded bg-green-100 text-green-700">
                            {t('settings.active')}
                          </span>
                        ) : (
                          <span className="text-xs px-2 py-0.5 rounded bg-gray-100 text-gray-600">
                            {t('settings.inactive')}
                          </span>
                        )}
                      </div>
                      <p className="text-sm text-muted-foreground">{user.email}</p>
                      {user.branchId && (
                        <p className="text-xs text-muted-foreground">
                          Branch: {
                            branches?.find((b) => b._id === user.branchId)
                              ? getLangValue(branches.find((b) => b._id === user.branchId)!.name)
                              : user.branchId
                          }
                        </p>
                      )}
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <button
                      onClick={() => openEditUser(user)}
                      className="p-2 hover:bg-secondary rounded transition-colors"
                    >
                      <Pencil className="h-4 w-4" />
                    </button>
                    <button
                      onClick={() => handleDeleteUser(user._id)}
                      className="p-2 hover:bg-destructive/10 text-destructive rounded transition-colors"
                    >
                      <Trash2 className="h-4 w-4" />
                    </button>
                  </div>
                </div>
              ))}

              {users?.length === 0 && (
                <div className="text-center py-12 text-muted-foreground">
                  <Users className="h-16 w-16 mx-auto mb-4 opacity-20" />
                  <p>{t('settings.noUsers')}</p>
                </div>
              )}
            </div>
          )}

          {/* Branches Tab */}
          {activeTab === 'branches' && (
            <div className="space-y-3">
              {branches?.map((branch) => (
                <div
                  key={branch._id}
                  className="bg-card rounded-lg border p-4 flex items-center justify-between"
                >
                  <div className="flex items-center gap-4">
                    <div
                      className={`w-10 h-10 rounded-full flex items-center justify-center ${
                        branch.type === 'warehouse'
                          ? 'bg-orange-100 text-orange-700'
                          : 'bg-blue-100 text-blue-700'
                      }`}
                    >
                      <Building2 className="h-5 w-5" />
                    </div>
                    <div>
                      <div className="flex items-center gap-2">
                        <p className="font-medium">{getLangValue(branch.name)}</p>
                        <span className="text-xs px-2 py-0.5 rounded bg-gray-100 text-gray-700">
                          {branch.code}
                        </span>
                        <span
                          className={`text-xs px-2 py-0.5 rounded ${
                            branch.type === 'warehouse'
                              ? 'bg-orange-100 text-orange-700'
                              : 'bg-blue-100 text-blue-700'
                          }`}
                        >
                          {branch.type === 'warehouse' ? 'Warehouse' : 'Branch'}
                        </span>
                        {branch.isActive ? (
                          <span className="text-xs px-2 py-0.5 rounded bg-green-100 text-green-700">
                            {t('settings.active')}
                          </span>
                        ) : (
                          <span className="text-xs px-2 py-0.5 rounded bg-gray-100 text-gray-600">
                            {t('settings.inactive')}
                          </span>
                        )}
                      </div>
                      <p className="text-sm text-muted-foreground">
                        {typeof branch.address === 'string'
                          ? branch.address
                          : `${branch.address.street}, ${branch.address.district}, ${branch.address.province}`}
                      </p>
                      <p className="text-xs text-muted-foreground">{branch.phone}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <button
                      onClick={() => openEditBranch(branch)}
                      className="p-2 hover:bg-secondary rounded transition-colors"
                    >
                      <Pencil className="h-4 w-4" />
                    </button>
                    <button
                      onClick={() => handleDeleteBranch(branch._id)}
                      className="p-2 hover:bg-destructive/10 text-destructive rounded transition-colors"
                    >
                      <Trash2 className="h-4 w-4" />
                    </button>
                  </div>
                </div>
              ))}

              {branches?.length === 0 && (
                <div className="text-center py-12 text-muted-foreground">
                  <Building2 className="h-16 w-16 mx-auto mb-4 opacity-20" />
                  <p>{t('settings.noBranches')}</p>
                </div>
              )}
            </div>
          )}

          {/* Roles Tab */}
          {activeTab === 'roles' && (
            <div className="space-y-3">
              {roles?.map((role) => (
                <div key={role.name} className="bg-card rounded-lg border p-4">
                  <div className="flex items-center gap-3 mb-3">
                    <div className={`w-10 h-10 rounded-full flex items-center justify-center ${getRoleColor(role.name)}`}>
                      <Shield className="h-5 w-5" />
                    </div>
                    <div>
                      <p className="font-medium">{role.label}</p>
                      <p className="text-xs text-muted-foreground">{role.name}</p>
                    </div>
                  </div>
                  <div className="flex flex-wrap gap-1">
                    {role.permissions.map((permission) => (
                      <span
                        key={permission}
                        className="text-xs px-2 py-1 rounded bg-secondary text-secondary-foreground"
                      >
                        {permission}
                      </span>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* User Modal */}
      {userModal.isOpen && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-card rounded-lg shadow-lg p-6 max-w-md w-full mx-4">
            <h2 className="text-xl font-bold mb-4">
              {userModal.mode === 'create' ? t('settings.createUser') : t('settings.editUser')}
            </h2>
            <div className="space-y-4">
              <div>
                <label className="text-sm font-medium">{t('settings.name')}</label>
                <input
                  type="text"
                  value={userForm.name}
                  onChange={(e) => setUserForm({ ...userForm, name: e.target.value })}
                  className="w-full mt-1 rounded-lg border bg-background px-3 py-2 text-sm"
                />
              </div>
              <div>
                <label className="text-sm font-medium">{t('auth.email')}</label>
                <input
                  type="email"
                  value={userForm.email}
                  onChange={(e) => setUserForm({ ...userForm, email: e.target.value })}
                  className="w-full mt-1 rounded-lg border bg-background px-3 py-2 text-sm"
                />
              </div>
              {userModal.mode === 'create' && (
                <div>
                  <label className="text-sm font-medium">{t('auth.password')}</label>
                  <input
                    type="password"
                    value={userForm.password}
                    onChange={(e) => setUserForm({ ...userForm, password: e.target.value })}
                    className="w-full mt-1 rounded-lg border bg-background px-3 py-2 text-sm"
                  />
                </div>
              )}
              <div>
                <label className="text-sm font-medium">{t('settings.role')}</label>
                <select
                  value={userForm.role}
                  onChange={(e) => setUserForm({ ...userForm, role: e.target.value })}
                  className="w-full mt-1 rounded-lg border bg-background px-3 py-2 text-sm"
                >
                  <option value="POS_OPERATOR">POS Operator</option>
                  <option value="BRANCH_SUPERVISOR">Branch Supervisor</option>
                  <option value="BRANCH_MANAGER">Branch Manager</option>
                  <option value="WAREHOUSE_STAFF">Warehouse Staff</option>
                  <option value="HQ_PURCHASING">HQ Purchasing</option>
                  <option value="HQ_ADMIN">HQ Admin</option>
                </select>
              </div>
              <div>
                <label className="text-sm font-medium">{t('settings.branch')}</label>
                <select
                  value={userForm.branchId}
                  onChange={(e) => setUserForm({ ...userForm, branchId: e.target.value })}
                  className="w-full mt-1 rounded-lg border bg-background px-3 py-2 text-sm"
                >
                  <option value="">{t('settings.noBranch')}</option>
                  {branches?.map((branch) => (
                    <option key={branch._id} value={branch._id}>
                      {getLangValue(branch.name)}
                    </option>
                  ))}
                </select>
              </div>
              <div className="flex items-center gap-2">
                <button
                  onClick={() => setUserForm({ ...userForm, isActive: !userForm.isActive })}
                  className="flex items-center gap-2"
                >
                  {userForm.isActive ? (
                    <ToggleRight className="h-5 w-5 text-green-600" />
                  ) : (
                    <ToggleLeft className="h-5 w-5 text-gray-400" />
                  )}
                  <span className="text-sm">{t('settings.active')}</span>
                </button>
              </div>
            </div>
            <div className="flex gap-2 mt-6">
              <button
                onClick={() => setUserModal({ isOpen: false, mode: 'create' })}
                className="flex-1 rounded-lg border px-4 py-2 text-sm font-medium hover:bg-secondary transition-colors"
              >
                {t('common.cancel')}
              </button>
              <button
                onClick={userModal.mode === 'create' ? handleCreateUser : handleUpdateUser}
                className="flex-1 rounded-lg bg-primary px-4 py-2 text-sm font-medium text-primary-foreground hover:bg-primary/90 transition-colors"
              >
                {t('common.save')}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Branch Modal */}
      {branchModal.isOpen && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-card rounded-lg shadow-lg p-6 max-w-md w-full mx-4">
            <h2 className="text-xl font-bold mb-4">
              {branchModal.mode === 'create'
                ? t('settings.createBranch')
                : t('settings.editBranch')}
            </h2>
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="text-sm font-medium">{t('settings.nameEn')}</label>
                  <input
                    type="text"
                    value={branchForm.nameEn}
                    onChange={(e) => setBranchForm({ ...branchForm, nameEn: e.target.value })}
                    className="w-full mt-1 rounded-lg border bg-background px-3 py-2 text-sm"
                  />
                </div>
                <div>
                  <label className="text-sm font-medium">{t('settings.nameTh')}</label>
                  <input
                    type="text"
                    value={branchForm.nameTh}
                    onChange={(e) => setBranchForm({ ...branchForm, nameTh: e.target.value })}
                    className="w-full mt-1 rounded-lg border bg-background px-3 py-2 text-sm"
                  />
                </div>
              </div>
              <div>
                <label className="text-sm font-medium">{t('settings.code')}</label>
                <input
                  type="text"
                  value={branchForm.code}
                  onChange={(e) => setBranchForm({ ...branchForm, code: e.target.value })}
                  className="w-full mt-1 rounded-lg border bg-background px-3 py-2 text-sm"
                />
              </div>
              <div>
                <label className="text-sm font-medium">{t('settings.address')}</label>
                <textarea
                  value={branchForm.address}
                  onChange={(e) => setBranchForm({ ...branchForm, address: e.target.value })}
                  rows={2}
                  className="w-full mt-1 rounded-lg border bg-background px-3 py-2 text-sm"
                />
              </div>
              <div>
                <label className="text-sm font-medium">{t('settings.phone')}</label>
                <input
                  type="text"
                  value={branchForm.phone}
                  onChange={(e) => setBranchForm({ ...branchForm, phone: e.target.value })}
                  className="w-full mt-1 rounded-lg border bg-background px-3 py-2 text-sm"
                />
              </div>
              <div>
                <label className="text-sm font-medium">{t('settings.type')}</label>
                <select
                  value={branchForm.type}
                  onChange={(e) =>
                    setBranchForm({ ...branchForm, type: e.target.value as 'branch' | 'warehouse' })
                  }
                  className="w-full mt-1 rounded-lg border bg-background px-3 py-2 text-sm"
                >
                  <option value="branch">Branch</option>
                  <option value="warehouse">Warehouse</option>
                </select>
              </div>
              <div className="flex items-center gap-2">
                <button
                  onClick={() =>
                    setBranchForm({ ...branchForm, isActive: !branchForm.isActive })
                  }
                  className="flex items-center gap-2"
                >
                  {branchForm.isActive ? (
                    <ToggleRight className="h-5 w-5 text-green-600" />
                  ) : (
                    <ToggleLeft className="h-5 w-5 text-gray-400" />
                  )}
                  <span className="text-sm">{t('settings.active')}</span>
                </button>
              </div>
            </div>
            <div className="flex gap-2 mt-6">
              <button
                onClick={() => setBranchModal({ isOpen: false, mode: 'create' })}
                className="flex-1 rounded-lg border px-4 py-2 text-sm font-medium hover:bg-secondary transition-colors"
              >
                {t('common.cancel')}
              </button>
              <button
                onClick={branchModal.mode === 'create' ? handleCreateBranch : handleUpdateBranch}
                className="flex-1 rounded-lg bg-primary px-4 py-2 text-sm font-medium text-primary-foreground hover:bg-primary/90 transition-colors"
              >
                {t('common.save')}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default SettingsPage;
